import { Component, Inject, Injector, TemplateRef, ViewChild } from '@angular/core';
import { PatientService } from 'app/core/http/services/patient.service';
import { Patient } from 'app/core/interfaces/patient';
import { BaseComponent } from '../base.component';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ScheduleRegistrationSecretaryComponent } from 'app/modules/secretaries/pages/schedule/registration/schedule-registration.component';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogData } from 'app/core/interfaces/dialog';
import { DateValidatorDirective } from 'app/core/helpers/dateValidatorDirective';
import { cpfValidator } from 'app/core/validators/CpfValidator';
import { phoneValidator } from 'app/core/validators/phoneValidator';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'schedule-select-patient',
  templateUrl: './schedule-select-patient.component.html',
  styleUrls: ['./schedule-select-patient.component.scss'],
})
export class ScheduleSelectPatientComponent extends BaseComponent {
  public search: string = '';
  public patients: Patient[] = [];
  public loadingPatients = false;
  public patientLoadError = '';

  @ViewChild('editPatientDialog') editPatientDialog!: TemplateRef<any>;

  newPatientForm: FormGroup;
  editPatientForm: FormGroup;
  showForm: boolean = false;
  editingPatient: Patient | null = null;
  private editDialogRef: MatDialogRef<any> | null = null;

  constructor(
    private patientService: PatientService,
    private injector: Injector,
    private fb: FormBuilder,
    private matDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    super(injector);

    this.newPatientForm = this.createPatientForm();
    this.editPatientForm = this.createPatientForm();
  }

  ngOnInit(): void {
    this.getPatient();
  }

  editPatient(patient: Patient, event?: Event) {
    this.openEditModal(patient, event);
  }

  openEditModal(patient: Patient, event?: Event) {
    event?.stopPropagation();
    this.editingPatient = patient;
    this.resetEditPatientFormDefaults();

    this.editPatientForm.patchValue({
      name: patient.name || '',
      cpf: patient.cpf || '',
      phone: this.normalizePhoneForForm(patient.phone),
      birthDate: patient.birthDate || '',
      gender: patient.gender || 'unknown',
      active: patient.active ?? true,
    });

    this.editDialogRef = this.matDialog.open(this.editPatientDialog, {
      width: '800px',
      maxWidth: '92%',
      minHeight: '60vh',
    });

    this.editDialogRef.afterClosed().subscribe(() => {
      this.editDialogRef = null;
      this.editingPatient = null;
      this.resetEditPatientFormDefaults();
    });
  }

  closeEditModal() {
    this.editDialogRef?.close();
  }

  requestCreatePatient() {
    this.layout
      .openDialog({
        showButtonCancel: true,
        buttonText: 'Confirmar Cadastro',
        title: 'Você confirma os dados desse paciente??',
        content: ' ',
      })
      .subscribe((confirm: boolean) => {
        if (confirm) this.createPatient();

        if (!confirm) {
          return;
        }
      });
  }

  createPatient() {
    const secretary = this.getUserLogged();

    const payload = this.newPatientForm.getRawValue();
    payload.created_by = {
      secretary_id: secretary.id,
      secretary_name: secretary.name,
    };

    this.patientService.createNew(payload).subscribe({
      next: (patient: Patient) => {
        this.showSuccessMessage = true;
        this.showErrorMessage = false;

        this.submitNewPatient(patient);
      },
      error: (err: HttpErrorResponse) => {
        console.log('error', err);

        this.loading = false;
        this.showErrorMessage = true;
        this.showSuccessMessage = false;

        this.messageError = err.error.message || err.message;
      },
    });
  }

  updatePatient() {
    if (!this.editingPatient) {
      return;
    }

    if (this.editPatientForm.invalid) {
      this.editPatientForm.markAllAsTouched();
      return;
    }

    const payload = this.editPatientForm.getRawValue();
    if (payload.phone) {
      payload.mobile = `55${payload.phone}`;
      payload.phone = `55${payload.phone}`;
    }

    const secretary = this.getUserLogged();

    payload.created_by = {
      secretary_id: secretary.id,
      secretary_name: secretary.name,
    };

    this.patientService.updateBySecretary(this.editingPatient.patient_id, secretary.id, payload).subscribe({
      next: () => {
        this.getPatient();
        this.closeEditModal();
        this.openSnackBar('Paciente atualizado com sucesso', 'success');
      },
      error: (err: HttpErrorResponse) => {
        this.openSnackBar(err.error.message || err.message, 'error');
      },
    });
  }

  getPatientPhoto(patient: Patient) {
    const photo = patient?.photo ? patient.photo : this.defaultPatientPhoto;

    return photo;
  }

  getPatientPhone(patient: Patient): string {
    const phone = patient?.phone || patient?.mobile || '';
    const digits = phone.replace(/\D/g, '');

    if (!phone || /undefined|null/i.test(phone) || digits.length < 10) {
      return 'Telefone não informado';
    }

    const nationalNumber = digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits;
    if (nationalNumber.length === 11) {
      return `(${nationalNumber.slice(0, 2)}) ${nationalNumber.slice(2, 7)}-${nationalNumber.slice(7)}`;
    }

    if (nationalNumber.length === 10) {
      return `(${nationalNumber.slice(0, 2)}) ${nationalNumber.slice(2, 6)}-${nationalNumber.slice(6)}`;
    }

    return phone;
  }

  filteredPatients(): Patient[] {
    const term = this.normalizeSearchText(this.search).replace(/\s+/g, '');
    if (!term) {
      return this.patients;
    }

    const digitsOnlyTerm = this.search.replace(/\D/g, '');
    const isNumericSearch = /^[\d\s().+-]+$/.test(this.search.trim());

    return this.patients.filter((patient) => {
      const searchableText = [patient.name, patient.email, patient.cpf, patient.phone, patient.mobile]
        .filter(Boolean)
        .map((value) => this.normalizeSearchText(value).replace(/\s+/g, ''));
      const matchesText = searchableText.some((value) => value.includes(term));

      if (matchesText || !isNumericSearch || !digitsOnlyTerm) {
        return matchesText;
      }

      const searchableDigits = [patient.cpf, patient.phone, patient.mobile]
        .filter(Boolean)
        .map((value) => value!.replace(/\D/g, ''));

      return searchableDigits.some((value) => value.includes(digitsOnlyTerm));
    });
  }

  trackByPatient(index: number, patient: Patient): string | number {
    return patient.patient_id || patient.cpf || index;
  }

  onClickSelectPatient(patient: Patient) {
    this.openScheduleRegistration(patient);
  }

  getPatient() {
    this.loadingPatients = true;
    this.patientLoadError = '';

    this.patientService.listNew().subscribe({
      next: (patients: Patient[]) => {
        this.patients = Array.isArray(patients) ? patients : [];
        this.loadingPatients = false;
      },
      error: () => {
        this.patientLoadError = 'Não foi possível carregar os pacientes. Tente novamente.';
        this.loadingPatients = false;
      },
    });
  }

  onNotFound() {
    this.showForm = true;
    this.resetNewPatientFormDefaults();
  }

  submitNewPatient(patient: Patient) {
    if (this.newPatientForm.valid) {
      this.openScheduleRegistration(patient);
    }
  }

  private openScheduleRegistration(patient: Patient) {
    if (!this.data?.param) {
      this.openSnackBar('Não foi possível iniciar o agendamento. Tente novamente.', 'error');
      return;
    }

    const registrationData: DialogData = {
      ...this.data,
      param: {
        ...this.data.param,
        patient,
      },
    };

    this.dialog.openComponentDialog(ScheduleRegistrationSecretaryComponent, registrationData);
  }

  private normalizeSearchText(value: string | undefined): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private normalizePhoneForForm(phone?: string) {
    const digits = (phone || '').replace(/\D/g, '');
    return digits.startsWith('55') ? digits.slice(2) : digits;
  }

  private createPatientForm(): FormGroup {
    return this.fb.group({
      name: new FormControl('', [Validators.required, Validators.pattern(/^[A-Za-zÀ-ÿ\s]+$/)]),
      cpf: new FormControl('', [Validators.required, cpfValidator]),
      phone: new FormControl('', [Validators.required, phoneValidator]),
      birthDate: new FormControl('', [Validators.nullValidator, DateValidatorDirective.validate]),
      gender: new FormControl('unknown', [Validators.nullValidator]),
      active: new FormControl(true, [Validators.required]),
    });
  }

  private resetNewPatientFormDefaults() {
    this.newPatientForm.reset({
      name: '',
      cpf: '',
      phone: '',
      birthDate: '',
      gender: 'unknown',
      active: true,
    });
    this.newPatientForm.markAsPristine();
    this.newPatientForm.markAsUntouched();
  }

  private resetEditPatientFormDefaults() {
    this.editPatientForm.reset({
      name: '',
      cpf: '',
      phone: '',
      birthDate: '',
      gender: 'unknown',
      active: true,
    });
    this.editPatientForm.markAsPristine();
    this.editPatientForm.markAsUntouched();
  }
}
